import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { CurrencyAmount } from './currencies';
import { WalletAddress } from './wallets';

type UserStatus = 'blocked' | 'not-validated' | 'pending' | 'validated';
type VerificationStatus = 'failed' | 'open' | 'pending' | 'verified';

export interface UserInfo {
    emailAddress: string;
    firstName: string;
    lastName: string;
    region: string;
    phoneNumber: string;
    registrationDate: string;
    activationDate: string;
    enabledIbanAccounts: IbanAccount[];
    enabledWalletAddresses: WalletAddress[];
    isBusiness: boolean;
    limits: {
        buy: UserLimit;
        sell: UserLimit;
    };
    status: {
        status: UserStatus;
        description: string;
        details: UserStatusDetails;
    };
}

export interface IbanAccount {
    holder: string;
    number: string;
    verified: boolean;
}

interface UserLimit {
    interval: string;
    limit: CurrencyAmount;
    remaining: CurrencyAmount;
}

interface UserStatusDetails {
    amld5VerificationStatus: VerificationStatus;
    emailAddressVerificationStatus: VerificationStatus;
    phoneNumberVerificationStatus?: VerificationStatus;
    identityVerificationStatus: VerificationStatus;
    privacyAgreementVerificationStatus: VerificationStatus;
    identityDocumentVerificationStatus: {
        documentType:
            | 'Passport'
            | 'IdentityCard'
            | 'DrivingLicence'
            | 'Selfie'
            | 'LivenessCheck'
            | 'ProofOfResidency'
            | 'ResidencePermit'
            | 'IbanVerification'
            | 'WalletVerification';
        documentSide: 'Front' | 'Back' | 'Selfie' | 'VideoProof' | 'SecondSelfie' | 'ProofOfResidency';
        status: 'missing' | 'pending' | 'present' | 'rejected';
    }[];
}

export const useUserInfoStore = defineStore('user', () => {
    const user = ref<UserInfo>(null);
    const iban = ref<IbanAccount>(null);

    function setUserInfo(userInfo: UserInfo): void {
        if (!userInfo) {
            user.value = null;
            return;
        }

        user.value = userInfo;
    }

    function selectIban(number: string): void {
        if (!number) {
            iban.value = null;
            return;
        }

        iban.value = user.value?.enabledIbanAccounts.find((account) => account.number === number) ?? null;
    }

    return {
        user: computed<UserInfo>(() => user.value),
        limits: computed<{ buy: UserLimit; sell: UserLimit }>(() => user.value?.limits ?? null),
        ibans: computed<IbanAccount[]>(() => user.value?.enabledIbanAccounts ?? null),
        selectedIban: computed<IbanAccount>(() => iban.value),
        setUserInfo,
        selectIban
    };
});
